import { defineStore } from "pinia";
import { AirlineOrder } from "./entity";
import { AirlineOrderList } from "./list.gen";
import type { AirlineOrderStore, AirlineOrderStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const airlineOrderList = new AirlineOrderList();
export const useAirlineOrderStore = defineStore("airlineOrderStore", {
  state: (): AirlineOrderStoreState => ({
    activeID: null,
    cache: {},
    list: [] as AirlineOrder[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-AirlineOrder", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.AirlineOrder"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(airlineOrder: AirlineOrder): Promise<void> {
      // Set active
      await airlineOrder
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = airlineOrder.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(airlineOrder: AirlineOrder): void {
      const id = airlineOrder.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(airlineOrder);

        // Set active
        if (this.active === null) {
          this.activeID = airlineOrder.id;
        }
      } else {
        this.reinsert(airlineOrder);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as AirlineOrder[]).map((x: any) => {
        const airlineOrder = new AirlineOrder({});
        airlineOrder.set(x as JSONData);
        return airlineOrder;
      });
    },
    async delete(airlineOrder: AirlineOrder): Promise<void> {
      await airlineOrder
        .delete()
        .then(() => {
          this.remove(airlineOrder);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const airlineOrder = (this.list as AirlineOrder[]).find(
        (x: AirlineOrder) => x.id === id,
      );
      if (airlineOrder !== undefined) {
        await this.delete(airlineOrder);
      } else {
        throw new Error("Could not find AirlineOrder with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as AirlineOrder[]).some(
        (x: AirlineOrder) => x.id === id,
      );
    },
    get(id: string): AirlineOrder | null {
      return (
        (this.list as AirlineOrder[]).find((x: AirlineOrder) => x.id === id) ||
        null
      );
    },
    reinsert(airlineOrder: AirlineOrder): void {
      const index = (this.list as AirlineOrder[]).findIndex(
        (x: AirlineOrder) => x.id === airlineOrder.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          airlineOrder,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(airlineOrder);
      }
      this.updateKey += 1;
    },
    remove({ id }: AirlineOrder): void {
      const index = (this.list as AirlineOrder[]).findIndex(
        (x: AirlineOrder) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<AirlineOrder> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const airlineOrder = this.get(id);
        if (airlineOrder !== null) {
          return airlineOrder;
        }
      }

      // Retrieve airlineOrder
      const airlineOrder = new AirlineOrder({});

      // Get airlineOrder
      await airlineOrder.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate airlineOrder
        await this.activate(entity);
      });

      return airlineOrder;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: AirlineOrder[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as AirlineOrder[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await airlineOrderList.getAll().then(async (list) => {
        list.forEach((airlineOrder) => {
          this.reinsert(airlineOrder);
        });

        // Activate airlineOrder
        if (list.length > 0) {
          if (airlineOrderList.active !== null) {
            this.activeID = airlineOrderList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("airline_order_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as AirlineOrder[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as AirlineOrder),
        };
      }

      // Retrieve list
      const items = await airlineOrderList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrder) => {
            this.reinsert(airlineOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrder) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as AirlineOrder),
        };
      }

      // Retrieve list
      const items = await airlineOrderList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrder) => {
            this.reinsert(airlineOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrder) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as AirlineOrder),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await airlineOrderList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrder) => {
            this.reinsert(airlineOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrder) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(airlineOrder: AirlineOrder): Promise<string> {
      // Update airlineOrder
      await airlineOrder.update();

      // Reinsert airlineOrder
      this.reinsert(airlineOrder);

      // Activate airlineOrder
      if (this.active === null) {
        await this.activate(airlineOrder);
      }

      this.emitStoreUpdateEvent();

      return airlineOrder.id;
    },
  },
  getters: {
    active(): AirlineOrder | null {
      if (this.activeID === null) {
        return null;
      }

      const airlineOrder = (this.list as AirlineOrder[]).find(
        (x: AirlineOrder) => x.id === this.activeID,
      );
      if (airlineOrder === undefined) {
        return null;
      }

      return airlineOrder;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAirlineOrderStore(): AirlineOrderStore {
  const store = useAirlineOrderStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AirlineOrderStore; // TODO: Fix this
}
